import React, { useState, useRef, useEffect } from 'react';
import styles from './HelpChatWidget.module.css';
import { askHelpBot } from '../../apis/helpApi';
import { BsChatDotsFill, BsSendFill, BsX } from 'react-icons/bs';
import ReactMarkdown from 'react-markdown';
import Draggable from 'react-draggable';

const HelpChatWidget = ({ hasNav }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: 'bot', content: '안녕하세요! tokplan 사용법이나 궁금한 점이 있으신가요? 무엇이든 물어보세요.' }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const nodeRef = useRef(null);
    const dragStart = useRef({ x: 0, y: 0 });
    const isDrag = useRef(false);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [messages, isOpen]);

    const handleSendMessage = async () => {
        if (!input.trim() || isLoading) return;

        const userMsg = input.trim();
        setInput('');

        const newMessages = [...messages, { role: 'user', content: userMsg }];
        setMessages(newMessages);
        setIsLoading(true);

        try {
            const historyPayload = messages.map(m =>
                `${m.role === 'user' ? '사용자' : '봇'}: ${m.content}`
            );
            const response = await askHelpBot(userMsg, historyPayload);
            const botAnswer = response.answer || response.message || JSON.stringify(response);
            setMessages(prev => [...prev, { role: 'bot', content: botAnswer }]);
        } catch (error) {
            setMessages(prev => [...prev, { role: 'bot', content: '죄송합니다. 오류가 발생했습니다. 다시 시도해주세요.' }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleDragStart = (e, data) => {
        dragStart.current = { x: data.x, y: data.y };
        isDrag.current = false;
    };

    const handleDrag = (e, data) => {
        if (!isDrag.current) {
            const dist = Math.hypot(data.x - dragStart.current.x, data.y - dragStart.current.y);
            if (dist > 5) {
                isDrag.current = true;
            }
        }
    };

    const handleStop = (e) => {
        // If it wasn't a drag, handle it as a click for the floating button
        if (!isDrag.current) {
            // Check if the clicked element is the floating button or inside it
            // We use the class name from styles
            const isButton = e.target.closest(`.${styles.floatingButton}`);
            if (isButton) {
                setIsOpen(prev => !prev);
            }
        }
        // Reset drag flag for next interaction
        isDrag.current = false;
    };

    return (
        <Draggable
            nodeRef={nodeRef}
            handle={`.${styles.dragHandle}`}
            onStart={handleDragStart}
            onDrag={handleDrag}
            onStop={handleStop}
        >
            <div
                ref={nodeRef}
                className={`${styles.widgetContainer} ${hasNav ? styles.withNav : ''}`}
            >
                {isOpen && (
                    <div className={styles.chatWindow}>
                        <div className={`${styles.header} ${styles.dragHandle}`}>
                            <div className={styles.headerTitle}>
                                <span>📚 가이드 봇</span>
                            </div>
                            <button
                                className={styles.closeButton}
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent bubble to Draggable stop if needed
                                    setIsOpen(false);
                                }}
                            >
                                <BsX size={40} />
                            </button>
                        </div>

                        <div className={styles.messagesArea}>
                            {messages.map((msg, index) => (
                                <div key={index} className={`${styles.messageRow} ${msg.role === 'user' ? styles.userRow : styles.botRow}`}>
                                    <div className={`${styles.messageBubble} ${msg.role === 'user' ? styles.userBubble : styles.botBubble}`}>
                                        {msg.role === 'bot' ? (
                                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                                        ) : (
                                            <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {isLoading && (
                                <div className={`${styles.messageRow} ${styles.botRow}`}>
                                    <div className={`${styles.messageBubble} ${styles.botBubble}`}>
                                        <span className={styles.typingDot}>...</span>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        <div className={styles.inputArea}>
                            <input
                                className={styles.input}
                                placeholder="질문을 입력하세요..."
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={isLoading}
                            />
                            <button
                                className={styles.sendButton}
                                onClick={handleSendMessage}
                                disabled={isLoading || !input.trim()}
                            >
                                <BsSendFill size={16} />
                            </button>
                        </div>
                    </div>
                )}

                <button
                    className={`${styles.floatingButton} ${styles.dragHandle}`}
                    onDoubleClick={() => setIsOpen(prev => !prev)}
                    aria-label="Help Chat"
                >
                    {isOpen ? <BsX size={32} /> : <BsChatDotsFill size={24} className={styles.buttonIcon} />}
                </button>
            </div>
        </Draggable>
    );
};

export default HelpChatWidget;
